import { useEffect } from "react";
import type { CSSProperties } from "react";
import { Mic, User } from "lucide-react";
import { ChatHistoryItem, type ThinkingBannerKind } from "../../types";
import {
  CHAT_PRIMARY,
  CHAT_PRIMARY_BG,
  CHAT_TEXT,
  CHAT_TEXT_MUTED,
} from "@/lib/chatTheme";
import logo from "../../assets/Beluga 1.png";
import "./chatHistory.css";

interface ChatHistoryProps {
  chatHistoryData: ChatHistoryItem[];
  isThinking: boolean;
  thinkingBanner?: ThinkingBannerKind;
  newChatHistory: String;
  scrollHeight: String;
  /** Fill remaining space inside a flex parent (use with parent max-height + flex column). */
  fillContainer?: boolean;
  hideThinkingText?: boolean;
}

export default function chatHistory({
  chatHistoryData,
  isThinking,
  thinkingBanner = "thinking",
  newChatHistory,
  scrollHeight,
  fillContainer = false,
  hideThinkingText = false,
}: ChatHistoryProps) {
  useEffect(() => {
    if (newChatHistory !== "" || isThinking) {
      const panels = document.getElementsByClassName("chat-panel");
      for (let i = 0; i < panels.length; i++) {
        panels[i].scrollTop = panels[i].scrollHeight;
      }
    }
  }, [newChatHistory, isThinking]);

  const thinkingLabel =
    thinkingBanner === "searching" ? "Searching..." : "Thinking...";

  const panelStyle: CSSProperties = fillContainer
    ? {
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        maxHeight: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        width: "100%",
        minWidth: 0,
        overscrollBehavior: "contain",
        gap: "16px",
      }
    : {
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${scrollHeight})`,
        maxHeight: `calc(100vh - ${scrollHeight})`,
        overflowY: "auto",
        overflowX: "hidden",
        width: "100%",
        minWidth: 0,
        overscrollBehavior: "contain",
        gap: "16px",
      };

  if (chatHistoryData.length === 0 && !isThinking) {
    return (
      <div className="chat-panel" style={panelStyle}>
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-2 py-8 text-center">
          <img
            src={logo}
            alt="Beluga"
            className="mb-3 h-16 w-16 object-contain sm:h-20 sm:w-20"
          />
          <p className="text-sm font-medium" style={{ color: CHAT_TEXT }}>
            Ask Beluga
          </p>
          <p className="mt-1 text-xs" style={{ color: CHAT_TEXT_MUTED }}>
            Start speaking to begin
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-panel" style={panelStyle}>
      {chatHistoryData.map((item: ChatHistoryItem, index) =>
        item.type === "input" ? (
          <div
            key={item.id ?? `in-${index}`}
            className="flex min-w-0 w-full items-start justify-end gap-2"
          >
            <div className="flex max-w-[min(100%,36rem)] min-w-0 flex-col items-end">
              <div
                className="min-w-0 shrink rounded-xl px-4 py-2.5 shadow-sm"
                style={{ backgroundColor: CHAT_PRIMARY, color: "white" }}
              >
                <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                  {item.text}
                </p>
              </div>
              <div className="mt-1 flex items-center gap-1">
                <Mic className="h-3 w-3" style={{ color: CHAT_TEXT_MUTED }} />
                <span className="text-xs" style={{ color: CHAT_TEXT_MUTED }}>
                  {item.time}
                </span>
              </div>
            </div>
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center self-start rounded-full"
              style={{ backgroundColor: CHAT_PRIMARY_BG }}
              aria-hidden
            >
              <User className="h-4 w-4" style={{ color: CHAT_PRIMARY }} />
            </div>
          </div>
        ) : (
          <div
            key={item.id ?? `out-${index}`}
            className="flex min-w-0 w-full items-start gap-2"
          >
            <img
              src={logo}
              alt=""
              className="h-8 w-8 shrink-0 self-start rounded-full object-contain"
              aria-hidden
            />
            <div className="flex max-w-[min(100%,36rem)] min-w-0 flex-col">
              <div
                className="min-w-0 shrink rounded-xl px-4 py-2.5 shadow-sm"
                style={{ backgroundColor: CHAT_PRIMARY_BG, color: CHAT_TEXT }}
              >
                <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                  {item.text}
                </p>
              </div>
              {item.time && (
                <span className="mt-1 text-xs" style={{ color: CHAT_TEXT_MUTED }}>
                  {item.time}
                </span>
              )}
            </div>
          </div>
        ),
      )}
      {isThinking && (
        <div className="flex min-w-0 w-full items-start gap-2">
          <img
            src={logo}
            alt=""
            className="h-8 w-8 shrink-0 self-start rounded-full object-contain"
            aria-hidden
          />
          <div
            className="max-w-[min(100%,36rem)] min-w-0 shrink rounded-xl px-4 py-2.5 shadow-sm"
            style={{ backgroundColor: CHAT_PRIMARY_BG, color: CHAT_TEXT }}
          >
            {!hideThinkingText && (
              <p className="mb-2 text-sm font-medium">{thinkingLabel}</p>
            )}
            <div className="dot-loader__dots">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
